import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiRefreshCw, FiPlus, FiLoader, FiCheckCircle, FiAlertCircle, FiClock, FiTrash2 } from 'react-icons/fi';

function normalizeStatus(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'ACTIVE') return 'APPROVED';
  if (s === 'IN_REVIEW' || s === 'PENDING') return 'IN REVIEW';
  return s || 'UNKNOWN';
}

function statusBadgeClass(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE') return 'bg-emerald-100 text-emerald-700';
  if (s === 'REJECTED' || s === 'DISABLED' || s === 'PAUSED') return 'bg-red-100 text-red-700';
  if (s === 'IN_REVIEW' || s === 'PENDING') return 'bg-amber-100 text-amber-700';
  return 'bg-neutral-100 text-neutral-600';
}

function statusIcon(status) {
  const s = String(status || '').toUpperCase();
  if (s === 'APPROVED' || s === 'ACTIVE') return <FiCheckCircle className="w-4 h-4" />;
  if (s === 'REJECTED' || s === 'DISABLED' || s === 'PAUSED') return <FiAlertCircle className="w-4 h-4" />;
  return <FiClock className="w-4 h-4" />;
}

function detectVariables(text) {
  const matches = text.match(/\{\{\d+\}\}/g) || [];
  return [...new Set(matches)].sort((a, b) => Number(a.replace(/\D/g, '')) - Number(b.replace(/\D/g, '')));
}

const MEDIA_TYPES = [
  { value: '', label: 'None' },
  { value: 'IMAGE', label: 'Image' },
  { value: 'VIDEO', label: 'Video' },
  { value: 'DOCUMENT', label: 'Document' },
  { value: 'LOCATION', label: 'Location' },
];

const BUTTON_KINDS = [
  { value: 'Custom', label: 'Custom' },
  { value: 'Visit website', label: 'Visit website' },
  { value: 'Call on WhatsApp', label: 'Call on WhatsApp' },
  { value: 'Call Phone Number', label: 'Call Phone Number' },
  { value: 'Copy offer code', label: 'Copy offer code' },
  { value: 'Share contact info', label: 'Share contact info' },
];

function newId() {
  return Math.random().toString(36).slice(2);
}

export default function WhatsAppMetaTemplateManager({ senderId, onSync }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en_US');
  const [category, setCategory] = useState('UTILITY');
  const [body, setBody] = useState('');
  const [samples, setSamples] = useState({});
  const [footer, setFooter] = useState('');
  const [mediaType, setMediaType] = useState('');
  const [mediaFields, setMediaFields] = useState({ handle: '', fileName: '', latitude: '', longitude: '', name: '', address: '' });
  const [mediaUploading, setMediaUploading] = useState(false);
  const [buttons, setButtons] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);

  const variables = useMemo(() => detectVariables(body), [body]);

  useEffect(() => {
    setSamples((prev) => {
      const next = {};
      variables.forEach((v) => {
        next[v] = prev[v] || '';
      });
      return next;
    });
  }, [variables]);

  const load = async () => {
    setLoading(true);
    try {
      const params = senderId ? { sender_id: senderId } : {};
      const { data } = await axios.get('/api/whatsapp/meta-templates', { params });
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
    } catch (e) {
      toast.error('Could not load Meta templates');
      console.error('Error loading meta templates:', e);
    } finally {
      setLoading(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post('/api/whatsapp/meta-templates/sync', { sender_id: senderId });
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
      if (onSync) await onSync();
    } catch (e) {
      toast.error('Could not sync Meta templates');
      console.error('Error syncing meta templates:', e);
    } finally {
      setSyncing(false);
    }
  };

  const setMediaField = (key, value) => setMediaFields((f) => ({ ...f, [key]: value }));

  const uploadMediaFile = async (file) => {
    if (!file) return;
    setMediaUploading(true);
    const form = new FormData();
    form.append('file', file);
    if (senderId) form.append('sender_id', senderId);
    try {
      const { data } = await axios.post('/api/whatsapp/upload-template-media', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMediaFields((f) => ({ ...f, handle: data.handle || '', fileName: file.name }));
      toast.success(`Media uploaded: ${file.name}`);
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Media upload failed';
      toast.error(msg);
      setMediaFields((f) => ({ ...f, handle: '', fileName: '' }));
    } finally {
      setMediaUploading(false);
    }
  };

  const addButton = (kind) => {
    setButtons((prev) => [
      ...prev,
      { id: newId(), kind, text: '', url: '', phoneNumber: '', offerCode: '', shareText: '' },
    ]);
  };

  const updateButton = (id, updates) => {
    setButtons((prev) => prev.map((b) => (b.id === id ? { ...b, ...updates } : b)));
  };

  const removeButton = (id) => setButtons((prev) => prev.filter((b) => b.id !== id));

  const addQuickReply = () => setQuickReplies((prev) => [...prev, { id: newId(), text: '' }]);

  const updateQuickReply = (id, text) => {
    setQuickReplies((prev) => prev.map((q) => (q.id === id ? { ...q, text } : q)));
  };

  const removeQuickReply = (id) => setQuickReplies((prev) => prev.filter((q) => q.id !== id));

  function buildClientComponents() {
    const components = [];

    if (mediaType) {
      const header = { type: 'HEADER', format: mediaType };
      if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(mediaType) && mediaFields.handle) {
        header.example = { header_handle: [mediaFields.handle] };
      }
      components.push(header);
    }

    const bodyComponent = { type: 'BODY', text: body.trim() };
    if (variables.length > 0) {
      bodyComponent.example = {
        body_text: [variables.map((v) => samples[v] || 'sample')],
      };
    }
    components.push(bodyComponent);

    if (footer.trim()) {
      components.push({ type: 'FOOTER', text: footer.trim() });
    }

    const allButtons = [];

    for (const b of buttons) {
      const text = (b.text || '').trim();
      if (!text) continue;
      const phone = (b.phoneNumber || '').trim();
      const url = (b.url || '').trim();
      const offer = (b.offerCode || '').trim();
      const share = (b.shareText || '').trim();
      let btn = { text };

      if (b.kind === 'Visit website' || (b.kind === 'Custom' && url)) {
        btn.type = 'URL';
        btn.url = url;
      } else if (b.kind === 'Call on WhatsApp' && phone) {
        const digits = phone.replace(/\D/g, '');
        btn.type = 'URL';
        btn.url = `https://wa.me/${digits}`;
      } else if (b.kind === 'Call Phone Number' && phone) {
        btn.type = 'PHONE_NUMBER';
        btn.phone_number = phone;
      } else if (b.kind === 'Copy offer code' && offer) {
        btn.type = 'COPY_CODE';
        btn.example = offer;
      } else if (b.kind === 'Share contact info' && (url || share)) {
        btn.type = 'URL';
        btn.url = url || `https://wa.me/?text=${encodeURIComponent(share)}`;
      } else if (b.kind === 'Custom' && phone) {
        btn.type = 'PHONE_NUMBER';
        btn.phone_number = phone;
      } else if (b.kind === 'Custom' && offer) {
        btn.type = 'COPY_CODE';
        btn.example = offer;
      } else {
        btn.type = 'QUICK_REPLY';
      }

      allButtons.push(btn);
    }

    for (const q of quickReplies) {
      const text = (q.text || '').trim();
      if (text) allButtons.push({ type: 'QUICK_REPLY', text });
    }

    if (allButtons.length > 0) {
      components.push({ type: 'BUTTONS', buttons: allButtons });
    }

    return components;
  }

  function validateForm() {
    if (['IMAGE', 'VIDEO', 'DOCUMENT'].includes(mediaType) && !mediaFields.handle) {
      toast.error('Please upload a media file for the selected media header');
      return false;
    }
    if (mediaType === 'LOCATION' && (!mediaFields.latitude.trim() || !mediaFields.longitude.trim())) {
      toast.error('Latitude and longitude are required for a location header');
      return false;
    }
    for (const b of buttons) {
      if (!b.text.trim()) {
        toast.error('All buttons need visible text');
        return false;
      }
      if (['Visit website', 'Share contact info'].includes(b.kind) && !b.url.trim() && !b.shareText.trim()) {
        toast.error(`${b.kind} button needs a URL or share text`);
        return false;
      }
      if (['Call on WhatsApp', 'Call Phone Number'].includes(b.kind) && !b.phoneNumber.trim()) {
        toast.error(`${b.kind} button needs a phone number`);
        return false;
      }
      if (b.kind === 'Copy offer code' && !b.offerCode.trim()) {
        toast.error('Copy offer code button needs an offer code');
        return false;
      }
      if (b.kind === 'Custom' && !b.url.trim() && !b.phoneNumber.trim() && !b.offerCode.trim()) {
        toast.error('Custom button needs a URL, phone number, or offer code');
        return false;
      }
    }
    return true;
  }

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim() || !body.trim()) return;
    if (!validateForm()) return;
    const components = buildClientComponents();
    setCreating(true);
    try {
      await axios.post('/api/whatsapp/meta-templates', {
        sender_id: senderId,
        name: name.trim(),
        language: language.trim(),
        category,
        components,
      });
      setShowForm(false);
      setName('');
      setBody('');
      setSamples({});
      setFooter('');
      setMediaType('');
      setMediaFields({ handle: '', fileName: '', latitude: '', longitude: '', name: '', address: '' });
      setButtons([]);
      setQuickReplies([]);
      await sync();
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Template creation failed';
      toast.error(msg);
      console.error('Error creating meta template:', e);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    // Auto-sync from Meta on load so status is always current
    sync().catch(() => load());
  }, [senderId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mt-6 pt-6 border-t border-neutral-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-neutral-900">Meta Templates</h3>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={sync}
            disabled={syncing}
            className="px-3 py-1.5 bg-white border border-neutral-300 text-neutral-700 rounded-lg text-xs font-semibold hover:bg-neutral-50 disabled:opacity-50 flex items-center gap-1"
          >
            {syncing ? <FiLoader className="w-3 h-3 animate-spin" /> : <FiRefreshCw className="w-3 h-3" />}
            Sync
          </button>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 flex items-center gap-1"
          >
            <FiPlus className="w-3 h-3" />
            Create
          </button>
        </div>
      </div>

      {showForm && (
        <form onSubmit={create} className="mb-4 space-y-3 bg-neutral-50 rounded-lg p-3 border border-neutral-200">
          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Template name</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. new_reply_notification"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Language</label>
              <input
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                placeholder="en_US"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-neutral-700 mb-1">Category</label>
              <select
                className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="UTILITY">UTILITY</option>
                <option value="MARKETING">MARKETING</option>
                <option value="AUTHENTICATION">AUTHENTICATION</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Body</label>
            <textarea
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Hello {{1}}, your order {{2}} is ready."
              rows={3}
              required
            />
            <p className="text-[10px] text-neutral-500 mt-1">
              Use {'{{1}}'}, {'{{2}}'} etc. for variables. Meta requires the message to be long enough for the number of variables.
            </p>
          </div>

          {variables.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-neutral-700">Sample values for Meta review</p>
              {variables.map((v) => (
                <div key={v} className="flex items-center gap-2">
                  <span className="text-xs font-mono text-neutral-600 w-10">{v}</span>
                  <input
                    className="flex-1 border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                    value={samples[v] || ''}
                    onChange={(e) => setSamples((s) => ({ ...s, [v]: e.target.value }))}
                    placeholder={`Sample for ${v}`}
                    required
                  />
                </div>
              ))}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Footer</label>
            <input
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={footer}
              onChange={(e) => setFooter(e.target.value)}
              placeholder="Optional footer text"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-neutral-700 mb-1">Media sample &middot; Optional</label>
            <select
              className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
              value={mediaType}
              onChange={(e) => {
                setMediaType(e.target.value);
                setMediaFields({ handle: '', fileName: '', latitude: '', longitude: '', name: '', address: '' });
              }}
            >
              {MEDIA_TYPES.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            {mediaType && ['IMAGE', 'VIDEO', 'DOCUMENT'].includes(mediaType) && (
              <div className="mt-2 space-y-2">
                <input
                  type="file"
                  accept={
                    mediaType === 'IMAGE' ? 'image/png,image/jpeg' :
                    mediaType === 'VIDEO' ? 'video/mp4' :
                    'application/pdf'
                  }
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white file:mr-3 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:font-semibold file:bg-emerald-50 file:text-emerald-700"
                  onChange={(e) => uploadMediaFile(e.target.files?.[0])}
                  disabled={mediaUploading}
                />
                {mediaUploading && (
                  <p className="text-xs text-neutral-500 flex items-center gap-1">
                    <FiLoader className="w-3 h-3 animate-spin" /> Uploading to Meta...
                  </p>
                )}
                {mediaFields.handle && (
                  <p className="text-xs text-emerald-600 truncate">
                    Uploaded: {mediaFields.fileName} (handle ready)
                  </p>
                )}
              </div>
            )}

            {mediaType === 'LOCATION' && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                  value={mediaFields.latitude}
                  onChange={(e) => setMediaField('latitude', e.target.value)}
                  placeholder="Latitude"
                  required
                />
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                  value={mediaFields.longitude}
                  onChange={(e) => setMediaField('longitude', e.target.value)}
                  placeholder="Longitude"
                  required
                />
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                  value={mediaFields.name}
                  onChange={(e) => setMediaField('name', e.target.value)}
                  placeholder="Location name"
                />
                <input
                  className="w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white"
                  value={mediaFields.address}
                  onChange={(e) => setMediaField('address', e.target.value)}
                  placeholder="Address"
                />
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-neutral-700">Buttons &middot; Optional</span>
              <div className="relative inline-block">
                <select
                  className="px-3 py-1.5 bg-white border border-neutral-300 rounded-lg text-xs font-semibold text-neutral-700"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      addButton(e.target.value);
                      e.target.value = '';
                    }
                  }}
                >
                  <option value="">+ Add button</option>
                  {BUTTON_KINDS.map((k) => (
                    <option key={k.value} value={k.value}>
                      {k.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {buttons.length > 0 && (
              <div className="space-y-2">
                {buttons.map((b) => (
                  <div key={b.id} className="bg-white border border-neutral-200 rounded-lg p-2 space-y-2">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
                      <select
                        className="w-full border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                        value={b.kind}
                        onChange={(e) => updateButton(b.id, { kind: e.target.value })}
                      >
                        {BUTTON_KINDS.map((k) => (
                          <option key={k.value} value={k.value}>
                            {k.label}
                          </option>
                        ))}
                      </select>
                      <div className="flex gap-2">
                        <input
                          className="flex-1 border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                          value={b.text}
                          onChange={(e) => updateButton(b.id, { text: e.target.value })}
                          placeholder="Button text"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => removeButton(b.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {['Custom', 'Visit website', 'Share contact info'].includes(b.kind) && (
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                        value={b.url}
                        onChange={(e) => updateButton(b.id, { url: e.target.value })}
                        placeholder={b.kind === 'Share contact info' ? 'URL or leave blank to build from share text' : 'URL'}
                      />
                    )}
                    {['Custom', 'Call on WhatsApp', 'Call Phone Number'].includes(b.kind) && (
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                        value={b.phoneNumber}
                        onChange={(e) => updateButton(b.id, { phoneNumber: e.target.value })}
                        placeholder="Phone number"
                      />
                    )}
                    {['Custom', 'Copy offer code'].includes(b.kind) && (
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                        value={b.offerCode}
                        onChange={(e) => updateButton(b.id, { offerCode: e.target.value })}
                        placeholder="Offer code"
                      />
                    )}
                    {b.kind === 'Share contact info' && (
                      <input
                        className="w-full border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                        value={b.shareText}
                        onChange={(e) => updateButton(b.id, { shareText: e.target.value })}
                        placeholder="Share text"
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-neutral-700">Quick reply &middot; Optional</span>
              <button
                type="button"
                onClick={addQuickReply}
                className="px-3 py-1.5 bg-white border border-neutral-300 rounded-lg text-xs font-semibold text-neutral-700 hover:bg-neutral-50 flex items-center gap-1"
              >
                <FiPlus className="w-3 h-3" />
                Add quick reply
              </button>
            </div>
            {quickReplies.length > 0 && (
              <div className="space-y-2">
                {quickReplies.map((q) => (
                  <div key={q.id} className="flex gap-2 items-center bg-white border border-neutral-200 rounded-lg p-2">
                    <span className="text-xs text-neutral-500 w-16">Custom</span>
                    <input
                      className="flex-1 border border-neutral-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                      value={q.text}
                      onChange={(e) => updateQuickReply(q.id, e.target.value)}
                      placeholder="Quick reply text"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => removeQuickReply(q.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <FiTrash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <p className="text-[10px] text-neutral-500 mt-1">
              Buttons help customers respond to your message or take action. You can add up to three quick reply buttons; if you add more than three buttons they will appear in a list.
            </p>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {creating ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />}
              Submit to Meta
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="px-4 py-2 bg-white border border-neutral-300 text-neutral-700 rounded-lg text-sm font-semibold hover:bg-neutral-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-sm text-neutral-500 flex items-center gap-2">
          <FiLoader className="w-4 h-4 animate-spin" /> Loading templates...
        </div>
      ) : templates.length === 0 ? (
        <p className="text-sm text-neutral-500">No synced templates. Click Sync to fetch from Meta.</p>
      ) : (
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {templates.map((t) => (
            <div key={`${t.name}-${t.language}`} className="border border-neutral-200 rounded-lg p-3 bg-white">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 truncate">{t.name}</p>
                  <p className="text-xs text-neutral-500">{t.language} &middot; {t.category || '—'}</p>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${statusBadgeClass(t.status)}`}>
                  {statusIcon(t.status)}
                  {normalizeStatus(t.status)}
                </span>
              </div>
              {t.rejection_reason && (
                <p className="mt-2 text-xs text-red-600">{t.rejection_reason}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
